import { Box, Button, Flex, Grid, Image, Input, Skeleton, Spinner } from '@chakra-ui/react';
import { ethers } from 'ethers';
import { useEffect, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { config, IconAssets } from 'src/config';
import { CARTRIDGE_ABI } from 'src/contracts/token/abi';
import { useBlockchainProvider } from 'src/providers/Blockchain/blockchainProvider';
import { Color } from 'src/theme/color';
import { TextSection } from '../TextSection/textSection';

const LoadingButton = () => (
	<Button
		variant="sharp"
		disabled
	>
		<Spinner />
	</Button>
);

const MigrationProgress = ({ percentage }: { percentage: number }) => {
	return (
		<Flex
			position="relative"
			borderRadius="4px"
			bg={Color.BLACK_3}
		>
			<Box
				bg={Color.HASHUP}
				transition="0.4s ease-out"
				h="100%"
				borderRadius="4px"
				w={`${percentage * 100}%`}
			/>
			<Box
				bg={Color.HASHUP}
				transition="0.4s ease-out"
				h="100%"
				w={`${percentage * 100}%`}
				filter="blur(10px)"
				pos="absolute"
			/>
		</Flex>
	);
};

enum MIGRATION_STEPS {
	NOT_STARTED,
	WITHDRAWAL,
	WITHDRAWN,
	SETTING_STORE,
	STORE_SET,
	APPROVAL,
	APPROVED,
	SALE,
	SALE_SET,
}

export const MigrationTab = ({ address, isInStore }: { address: string; isInStore: boolean }) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { account, contracts } = useBlockchainProvider();
	const [amount, setAmount] = useState<number>(0);

	const [currentStep, setCurrentStep] = useState<MIGRATION_STEPS>(MIGRATION_STEPS.NOT_STARTED);

	const cartridgeContract = useMemo(() => {
		const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
		if (provider) {
			const signer = provider.getSigner();
			return new ethers.Contract(address, CARTRIDGE_ABI, signer);
		} else {
			return null;
		}
	}, [window.ethereum]);

	const [showStoreError, setShowStoreError] = useState<boolean>(false);

	const fixStore = async () => {
		if (cartridgeContract) {
			const tx = cartridgeContract.setStore('0x1afb90451abbf5eb6f59842caf8949374f1b4683').then(() => {
				setShowStoreError(false);
			});
			await tx.wait();
		}
	};

	useEffect(() => {
		if (cartridgeContract) {
			cartridgeContract.store().then((res: any) => {
				if (res == '0x9741708b2DeDE32c972D3EF7959Fa3262860E485') {
					setShowStoreError(true);
				}
			});
		}
	}, [cartridgeContract]);

	const listOnNewStore = async () => {
		if (cartridgeContract && contracts.hashupStoreV2 && contracts.hashupStore) {
			setCurrentStep(MIGRATION_STEPS.SALE);

			try {
				const balance = await cartridgeContract.balanceOf(account);
				const prevPrice = await contracts.hashupStore.getCartridgePrice(cartridgeContract.address);

				const transaction = await contracts.hashupStoreV2.sendLicenseToStore(
					cartridgeContract.address,
					prevPrice,
					balance <= 1000000 ? balance : 1000000,
					20
				);

				await transaction.wait();
				setCurrentStep(MIGRATION_STEPS.SALE_SET);
			} catch (e) {
				setCurrentStep(MIGRATION_STEPS.APPROVED);
			}
		}
	};

	const removeCartridges = async () => {
		if (contracts.hashupStore) {
			setCurrentStep(MIGRATION_STEPS.WITHDRAWAL);
			try {
				const tx = await contracts.hashupStore.withdrawCartridges(address, ethers.constants.MaxUint256);
				await tx.wait();
				setCurrentStep(MIGRATION_STEPS.WITHDRAWN);
			} catch (e: any) {
				setCurrentStep(MIGRATION_STEPS.NOT_STARTED);
			}
		}
	};

	const approveNewStore = async () => {
		if (cartridgeContract) {
			setCurrentStep(MIGRATION_STEPS.APPROVAL);
			try {
				const tx = await cartridgeContract.approve(
					config.blockchain.contracts.storeV2,
					ethers.constants.MaxUint256
				);
				await tx.wait();
				setCurrentStep(MIGRATION_STEPS.APPROVED);
			} catch (e: any) {
				setCurrentStep(MIGRATION_STEPS.STORE_SET);
			}
		}
	};

	useEffect(() => {
		console.log('Store', isInStore);
	}, [isInStore]);

	const changeStore = async () => {
		if (cartridgeContract) {
			setCurrentStep(MIGRATION_STEPS.SETTING_STORE);
			try {
				const tx = await cartridgeContract.setStore(config.blockchain.contracts.storeV1);
				await tx.wait();
				setCurrentStep(MIGRATION_STEPS.STORE_SET);
			} catch (e: any) {
				console.log(e);
				setCurrentStep(MIGRATION_STEPS.WITHDRAWN);
			}
		}
	};

	const currentPage = {
		[MIGRATION_STEPS.NOT_STARTED]: () => (
			<Button
				variant="sharp"
				onClick={removeCartridges}
			>
				{t('yourGames.migration.withdraw')}
			</Button>
		),
		[MIGRATION_STEPS.WITHDRAWAL]: () => <LoadingButton />,
		[MIGRATION_STEPS.WITHDRAWN]: () => (
			<Button
				variant="sharp"
				onClick={changeStore}
			>
				{t('yourGames.migration.change')}
			</Button>
		),
		[MIGRATION_STEPS.SETTING_STORE]: () => <LoadingButton />,
		[MIGRATION_STEPS.STORE_SET]: () => (
			<Button
				variant="sharp"
				onClick={approveNewStore}
			>
				{t('yourGames.migration.approve')}
			</Button>
		),
		[MIGRATION_STEPS.APPROVAL]: () => <LoadingButton />,
		[MIGRATION_STEPS.APPROVED]: () => (
			<Button
				variant="sharp"
				onClick={listOnNewStore}
			>
				{t('yourGames.migration.send')}
			</Button>
		),
		[MIGRATION_STEPS.SALE]: () => <LoadingButton />,
		[MIGRATION_STEPS.SALE_SET]: () => <Box>{t('yourGames.migration.saleSet')}</Box>,
	};

	useEffect(() => {
		if (
			contracts.hashupStore &&
			(currentStep == MIGRATION_STEPS.SALE_SET || currentStep == MIGRATION_STEPS.NOT_STARTED)
		) {
			//setIsInStore(true);
			setIsLoading(false);
		}
	}, [currentStep]);

	return (
		<Flex
			justifyContent="space-between"
			gap="20px"
			flexDir="column"
		>
			<TextSection heading={t('yourGames.migration.heading')}>{t('yourGames.migration.description')}</TextSection>
			{!isLoading ? (
				isInStore ? (
					<>
						<Flex
							align="center"
							gap="15px"
						>
							{' '}
							<Image
								w="30px"
								src={IconAssets.copyAdressConfirmed}
							/>{' '}
							{t('yourGames.migration.newestInfo')}
						</Flex>
					</>
				) : (
					<Grid
						templateColumns="250px 1fr"
						gap="10px"
					>
						{currentPage[currentStep]()}
						<MigrationProgress percentage={currentStep / MIGRATION_STEPS.SALE_SET} />
					</Grid>
				)
			) : (
				<Skeleton />
			)}
			{showStoreError && <Button onClick={fixStore}>Fix cartridge</Button>}
		</Flex>
	);
};
